import './DismissiblePill.scss';

import React, { useCallback, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import PaletteDismissiblePill from 'web-palette/dist/components/DismissiblePill/DismissiblePill';

import { spreadProps } from '@ulta/core/utils/dom/dom';

import ReviewStarsCard from '@ulta/components/ReviewStarsCard/ReviewStarsCard';

import { FILTER_GROUPS } from '@ulta/utils/constants/filters';

import * as utils from './DismissiblePill';

/**
  * Represents an DismissiblePill component
  * @method
  * @param {DismissiblePillProps} props - React properties passed from composition
  * @returns PaletteDismissiblePill
  */

const DismissiblePill = ( props ) => {
  const PalDismissiblePill = PaletteDismissiblePill.default || PaletteDismissiblePill;
  const dismissiblePillRef = useRef( null );

  const handleOnClick = useCallback( () => {
    props.onClick();
  }, [props] );

  const dismissiblePillProps = utils.prepareDismissiblePillProps( { props }, { handleOnClick } );

  return (
    <div
      className='DismissiblePill'
      ref={ dismissiblePillRef }
    >
      <PalDismissiblePill
        { ...spreadProps( { ...dismissiblePillProps } ) }
      />
    </div>
  );
};

/**
 * prepareDismissiblePillProps is a function that parepares properties for the PaletteDismissiblePill component.
 * @method
 * @param {object} data - An object containing props.
 * @param {object} methods - An object containing handler functions.
 * @returns {object} - Returns an object with prepared properties for the PaletteDismissiblePill component.
 */

export const prepareDismissiblePillProps = ( data, methods ) => {
  const { props } = data || {};
  const { handleOnClick } = methods || {};

  if( !props ){
    return {};
  }

  const { colorCode, colorImage, rating, itemText, groupType, loading, ariaLabel } = props;

  let externalMedia = null;
  const imageUrl = colorImage?.imageUrl ?? '';
  const swatchImage = imageUrl ? { imageUrl } : null;

  let specificProps = {};

  if( groupType === FILTER_GROUPS.RATING ){
    externalMedia = (
      <div className='DismissiblePill__reviews'>
        <ReviewStarsCard rating={ rating } />
      </div>
    );
    specificProps = { externalMedia };
  }
  else if( colorCode ){
    externalMedia = (
      <SwatchColorCode
        colorCode={ colorCode }
      />
    );
    specificProps = { externalMedia, message: itemText };
  }
  else if( swatchImage ){
    specificProps = { swatchImage, message: itemText };
  }
  else {
    specificProps = { message: itemText };
  }

  return {
    ...props,
    ...specificProps,
    externalPillAction: handleOnClick,
    disabled: loading,
    ariaDescribedBy: ariaLabel
  };
};

const SwatchColorCode = ( { colorCode } ) => (
  <span className='Swatch Swatch__circle'>
    <span className='Swatch__div'
      style={ { background: colorCode } }
    ></span>
  </span>
);

/**
 * Property type definitions
 * @typedef DismissiblePillProps
 * @type {object}
 * @property {string} actionType - Sets the type of action ( button or link )
 * @property {boolean} active - Sets the selected state for button/link checked state
 * @property {string} ariaDescribedBy - sets the aria-describedby attribute for button or input elements
 * @property {boolean} disabled - Sets the disabled state
 * @property {func} externalPillAction - Sets the onClick for button or link
 * @property {string} message - Sets the message to be displayed in the pill
 * @property {any} ref - Sets the ref of the dismissible pull
 * @property {string} screenReaderText - Sets the screenReaderText for the pill
 * @property {number} tabIndex - Sets the tabIndex for the button or input elements
 * @property {boolean} unavailable - Sets the unavailable state for button/link checked state
 */

export const propTypes = {
  /**
   * Sets the type of action ( button or link )
   */
  actionType: PropTypes.oneOf( ['button', 'link'] ),
  /**
   * Sets the active state for button/link checked state
   */
  active: PropTypes.bool,
  /**
   * Sets the aria-describedby attribute for button or input elements
   */
  ariaDescribedBy: PropTypes.string,
  /**
   * Sets the disabled state
   */
  disabled: PropTypes.bool,
  /**
   * Sets the onClick for button or link
   */
  externalPillAction: PropTypes.func,
  /**
   * Sets the message to be displayed in the pill
   */
  message: PropTypes.string,
  /**
   * pill reference
   */
  ref: PropTypes.any,
  /**
   * Sets the screenReaderText for the pill
   */
  screenReaderText: PropTypes.string,
  /**
   * Sets the tab-index of the button or input element
   */
  tabIndex: PropTypes.number,
  /**
   * Sets the unavailable state for button/link checked state
   */
  unavailable: PropTypes.bool
};

DismissiblePill.propTypes = propTypes;

export default DismissiblePill;